import {
  BookOpen,
  Compass,
  CheckCircle,
  BarChart3,
  BookA,
  Mail,
  Settings,
  ShieldCheck,
  FileText,
} from 'lucide-react';
import HelpSection from '@/components/help/HelpSection';
import Button from '@/components/ui/Button';

function Subheading({ children }: { children: React.ReactNode }) {
  return <h4 className="font-medium text-gray-900 mt-6 mb-2">{children}</h4>;
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 mb-6">
      <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-bold">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
        <div className="text-sm text-gray-600 space-y-2">{children}</div>
      </div>
    </div>
  );
}

function Term({ term, definition }: { term: string; definition: React.ReactNode }) {
  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <dt className="font-medium text-gray-900">{term}</dt>
      <dd className="mt-0.5 text-sm text-gray-600">{definition}</dd>
    </div>
  );
}

function StatusDot({ colour, label }: { colour: string; label: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`inline-block h-3 w-3 rounded-full ${colour}`} />
      {label}
    </li>
  );
}

export default function HelpPage() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Help Centre</h1>
        <p className="mt-1 text-sm text-gray-500">
          Everything you need to classify and submit your group cash flow in Ashcourt Cashflow
        </p>
      </div>

      <div className="space-y-4">

        {/* 1. Quick tour */}
        <HelpSection title="What is Ashcourt Cashflow?" icon={<Compass className="h-5 w-5" />} defaultOpen>
          <p className="text-sm text-gray-600 mb-4">
            Ashcourt Cashflow replaces the manual Excel-based process for classifying cash flow
            movements across the group. Each entity enters its own classified cash flow lines
            directly, and Finance sees a live consolidated view that feeds through to Qlik on each
            reload.
          </p>

          <Subheading>The problem it solves</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            Qlik and Sage record balance-sheet movements, but not the detail needed to classify
            items such as capex, HP repayments, loan drawdowns, dividends, and exceptional items.
            Previously one person re-keyed those classifications centrally into a spreadsheet.
            Cashflow moves that responsibility to the entities themselves, against a controlled list
            of CF codes, and writes the results back to Qlik automatically.
          </p>

          <Subheading>The two types of cash flow line</Subheading>
          <ul className="text-sm text-gray-600 space-y-2 mb-4">
            <li>
              <strong>Derived lines (CF1000–CF4999)</strong> — read automatically from Qlik. These
              represent balance-sheet movements (debtors, creditors, group balances, finance
              movements). You cannot edit them; they appear in the <strong>Qlik</strong> column.
            </li>
            <li>
              <strong>Classified lines (CF5000+)</strong> — entered by entity preparers. These cover
              capex, HP &amp; loans, acquisitions, exceptional items, and tax. They appear in the{' '}
              <strong>Entry</strong> column with a blue input field.
            </li>
          </ul>

          <Subheading>The workflow in brief</Subheading>
          <p className="text-sm text-gray-600">
            Preparer enters classified lines → submits for review → Reviewer agrees
            → the entity turns green on the Validation Board → Qlik picks up the output file on its
            next reload.
          </p>
        </HelpSection>

        {/* 2. Entering data */}
        <HelpSection title="Entering your cash flow (Preparers)" icon={<BookOpen className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            As a Preparer for an entity your job is to enter the classified cash flow lines for your
            entity each period and submit them for review. The reconciliation bar at the top of the
            page shows your running balance — aim to get the <strong>Unreconciled</strong> figure to zero
            and <strong>CF9999</strong> to zero before submitting.
          </p>

          <Step number={1} title="Navigate to your entity">
            <p>
              Click <strong>Entities</strong> in the sidebar. Select the period from the dropdown
              at the top right, then click the eye icon on your entity row — or click anywhere on
              the row — to open its cash flow page.
            </p>
          </Step>

          <Step number={2} title="Understand the statement layout">
            <p>
              The statement is divided into sections. Derived lines (from Qlik) show their values
              in the <strong>Qlik</strong> column in plain text. Classified lines show a{' '}
              <strong>blue Entry field</strong> — that is where you type.
            </p>
            <p>
              The column headers are: <strong>Code / Description</strong> |{' '}
              <strong>Notes</strong> | <strong>Sign</strong> | <strong>Qlik</strong> |{' '}
              <strong>Entry</strong>.
            </p>
          </Step>

          <Step number={3} title="Enter classified line values">
            <p>
              Click into any blue Entry field and type the amount in pounds (e.g. <code>12500</code>{' '}
              or <code>-4200</code>) using the keyboard — the field deliberately ignores the mouse
              scroll wheel. The value saves when you press Enter or tab/click away, and the
              reconciliation bar at the top updates immediately.
            </p>
            <p>
              The <strong>Sign</strong> column (+ or −) shows the sign convention for that code —
              cash inflows are positive, cash outflows are negative. Enter the value as a positive
              number if it is a cash outflow on a − line; enter it negative to reverse.
            </p>
          </Step>

          <Step number={4} title="Add notes (optional)">
            <p>
              Each classified line has a <strong>Notes</strong> field next to the Entry column. Use
              it to explain the figure — for example the counterparty, asset description, or loan
              reference. Notes save automatically. The field shows <em>Enter amount first</em> until
              you have typed a value for that line.
            </p>
          </Step>

          <Step number={5} title="Check the reconciliation">
            <p>
              The sticky bar at the top of the page shows four figures:
            </p>
            <ul className="list-disc ml-4 space-y-1 mt-1">
              <li><strong>Unreconciled</strong> — the unreconciled cash balance: Net cash change minus the bank movement from Qlik. Aim for zero; submitting or agreeing while non-zero brings up a warning first.</li>
              <li><strong>CF9999</strong> — must be zero. Any unclassified historic balance sits here.</li>
              <li><strong>Net cash change</strong> — total of all classified and derived lines.</li>
              <li><strong>BS movement</strong> — the actual movement on the bank and cash accounts, read from Qlik.</li>
            </ul>
            <p className="mt-2">
              A green tick appears next to Unreconciled and CF9999 when they reach zero. You need both
              green before the Validation Board will show your entity as balanced.
            </p>
          </Step>

          <Step number={6} title="Submit for review">
            <p>
              When Unreconciled and CF9999 are both zero, click the <strong>Submit</strong> button in
              the top right. The status changes to <em>Submitted</em> and the entry fields lock. A
              reviewer can then agree the submission.
            </p>
            <p>
              Entities that are already fully reconciled with nothing to enter (e.g. dormant
              companies) can be submitted straight from <em>Not Started</em>. If you submit while
              the statement is not fully reconciled, a warning lists what is outstanding and asks
              you to confirm.
            </p>
          </Step>
        </HelpSection>

        {/* 3. Reviewing */}
        <HelpSection title="Reviewing & agreeing (Reviewers)" icon={<ShieldCheck className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            As a Reviewer for an entity or division your job is to inspect submitted entity cash flows and agree them
            once you are satisfied. You can also revert a submission back to In Progress if the
            preparer needs to make changes.
          </p>

          <Subheading>Finding submitted entities</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            The <strong>Validation Board</strong> (sidebar) gives you a colour-coded matrix of every
            entity across periods. Amber cells are submitted and waiting for your agreement. Click
            any cell to jump directly to that entity's cash flow page.
          </p>
          <p className="text-sm text-gray-600 mb-4">
            The <strong>Entities</strong> page also shows each entity's status for the selected
            period — use the period dropdown to switch.
          </p>

          <Subheading>Agreeing a submission</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            Open the entity's cash flow page. Review the classified lines and their notes. When you
            are satisfied, click <strong>Agree</strong>. The status moves to{' '}
            <em>Agreed</em> and the cell turns green on the Validation Board.
          </p>

          <Subheading>Reverting a submission</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            If something needs correcting, click <strong>Revert</strong> next to the Agree button.
            This moves the status back to <em>In Progress</em> so the preparer can edit and
            re-submit. The entry fields become editable again on the preparer's side.
          </p>

          <Subheading>Reopening an agreed cash flow</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            An <em>Agreed</em> cash flow can be reopened with the <strong>Reopen</strong> button.
            This clears the agreement and returns it to <em>In Progress</em> — it must then be
            re-submitted and re-agreed.
          </p>

          <Subheading>Agreeing while unreconciled</Subheading>
          <p className="text-sm text-gray-600">
            Agreeing a cash flow that is not fully reconciled brings up the same warning the
            preparer sees on submit, listing the outstanding difference and any CF9999 balance.
          </p>
        </HelpSection>

        {/* 4. Validation board */}
        <HelpSection title="Validation Board" icon={<BarChart3 className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            The Validation Board at <strong>Validation Board</strong> in the sidebar shows a
            colour-coded matrix — entities as rows, periods as columns. Use the period dropdown to
            navigate to the period you care about.
          </p>

          <Subheading>Cell colours</Subheading>
          <ul className="text-sm text-gray-600 space-y-2 mb-4">
            <StatusDot colour="bg-green-500" label={<><strong>Green</strong> — Agreed and balanced. CF9999 is zero.</>} />
            <StatusDot colour="bg-amber-400" label={<><strong>Amber</strong> — Submitted, pending reviewer agreement.</>} />
            <StatusDot colour="bg-blue-400" label={<><strong>Blue</strong> — In Progress. Preparer is entering data.</>} />
            <StatusDot colour="bg-red-500" label={<><strong>Red</strong> — Submitted but not balanced, or CF9999 is non-zero. Needs correction before it can be agreed.</>} />
            <StatusDot colour="bg-gray-200 border border-gray-300" label={<><strong>Grey</strong> — Not started. No data entered yet.</>} />
          </ul>

          <Subheading>Division rows</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            Click a division row to collapse or expand its entities. A division shows{' '}
            <strong>green</strong> only when every entity in it is agreed and balanced,{' '}
            <strong>amber</strong> when every entity is at least submitted (awaiting agreement),
            and red otherwise.
          </p>

          <Subheading>Live updates</Subheading>
          <p className="text-sm text-gray-600">
            The board refreshes itself every 30 seconds and whenever you return to the tab, so
            submissions and agreements made by other users appear without reloading the page.
          </p>

          <Subheading>Summary bar</Subheading>
          <p className="text-sm text-gray-600">
            Above the board, a summary bar shows how many entities and divisions are green for the
            selected period.
          </p>
        </HelpSection>

        {/* 5. CF codes */}
        <HelpSection title="CF codes explained" icon={<FileText className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            CF codes are the classification scheme used by Qlik. Each code maps to a specific type
            of cash flow movement, in two groups:
          </p>

          <Subheading>Derived lines (read-only)</Subheading>
          <p className="text-sm text-gray-600 mb-2">
            These are calculated by Qlik from balance-sheet movements and loaded automatically.
            They appear with a lock icon and cannot be edited in Cashflow.
          </p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-4 list-disc">
            <li><strong>CFP0500 &amp; CFP1000–CFP1030</strong> — Operating profit &amp; non-cash adjustments (PBT, depreciation, amortisation, interest, asset-sale add-backs) from the Qlik P&amp;L</li>
            <li><strong>CF1000–CF1070</strong> — Working capital: debtors &amp; stock</li>
            <li><strong>CF2000–CF2020</strong> — Amounts due from group</li>
            <li><strong>CF3000–CF3060</strong> — Working capital: creditors</li>
            <li><strong>CF4000–CF4030</strong> — Finance &amp; other movements</li>
            <li><strong>CF0100</strong> — Cash &amp; bank movement. Not a statement line: it is the figure the statement reconciles <em>to</em>, shown as "BS movement" in the reconciliation bar.</li>
          </ul>

          <Subheading>Classified lines (entry required)</Subheading>
          <p className="text-sm text-gray-600 mb-2">
            These are entered by entity preparers. They appear with a blue Entry field.
          </p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-4 list-disc">
            <li><strong>CF5000–CF5040</strong> — Capex &amp; asset purchases</li>
            <li><strong>CF6000–CF6540</strong> — HP &amp; loan repayments / drawdowns</li>
            <li><strong>CF7000–CF7750</strong> — Acquisitions &amp; distributions</li>
            <li><strong>CF8000–CF8510</strong> — Exceptional items &amp; tax</li>
            <li><strong>CF9999</strong> — Catch-all / unclassified. <strong>Must be zero</strong> before the entity can be agreed.</li>
          </ul>

          <Subheading>Codes must exist in the Qlik report mapping</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            Qlik's cash flow report only shows codes listed in its mapping workbook (the
            CFlow_Group sheet). A code that exists here but not there will load into Qlik and
            silently never appear. The app checks the live mapping on every Qlik sync: unmapped
            codes get a red <strong>Not in Qlik mapping</strong> badge on Admin &gt; CF Codes, a
            red warning icon on the entry screen, and a tile on Admin &gt; System Dashboard counting them and the
            submitted value affected. Fix by adding the code to the mapping workbook or retiring it.
          </p>

          <Subheading>CF9999 — the validation rule</Subheading>
          <p className="text-sm text-gray-600">
            CF9999 acts as a catch-all for any historic unclassified balance. If CF9999 is non-zero,
            the entity will show red on the Validation Board even if the Unreconciled figure is zero. The
            fix is to reclassify the balance across the appropriate CF5xxx–CF8xxx codes so CF9999
            returns to zero.
          </p>
        </HelpSection>

        {/* 6. Admin */}
        <HelpSection title="Admin" icon={<Settings className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            Admin screens are accessible from the <strong>Admin</strong> section of the sidebar and
            are restricted to users with the Admin role.
          </p>

          <Subheading>Users</Subheading>
          <p className="text-sm text-gray-600 mb-2">
            At <strong>Admin &gt; Users</strong> you can invite new users, change roles, and
            deactivate or delete accounts.
          </p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-4 list-disc">
            <li><strong>Invite</strong> — sends a branded email with a 24-hour activation link. The recipient sets their own password.</li>
            <li><strong>Edit (pencil)</strong> — change a user's role.</li>
            <li><strong>Access (key icon)</strong> — assign the user to entities and/or divisions, with a role per assignment (see Roles &amp; permissions below).</li>
            <li><strong>Disable (ban icon)</strong> — deactivates the account and immediately revokes all active sessions. The user cannot log in but their data is preserved.</li>
            <li><strong>Delete (bin icon)</strong> — permanently removes the user. Only succeeds for users with no associated data (e.g. an accidentally invited user who never activated). For everyone else, disable instead.</li>
          </ul>

          <Subheading>Entities &amp; Divisions</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            At <strong>Admin &gt; Entities</strong> you can hide entities or whole divisions from
            the Validation Board and lists (display-only and reversible — figures are unaffected),
            map each entity's <strong>Qlik name</strong> (required before its derived lines can
            populate), and trigger an immediate sync from the month-end close app. Entity and
            division master data itself is owned by the month-end app and syncs across hourly.
          </p>

          <Subheading>CF Codes</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            At <strong>Admin &gt; CF Codes</strong> you can view the full code list, add new codes,
            and deactivate codes that are no longer in use. The list is seeded with the 46 standard
            codes; contact Finance before making changes as the list must stay in sync with Qlik.
          </p>

          <Subheading>Statement Layout</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            At <strong>Admin &gt; Statement Layout</strong> you can configure the order and section
            groupings that appear on the cash flow entry page. Changes take effect immediately for
            all users.
          </p>

          <Subheading>Exchange Runs (Qlik sync)</Subheading>
          <p className="text-sm text-gray-600 mb-4">
            At <strong>Admin &gt; Exchange Runs</strong> you can see the history of Qlik CSV
            exchanges — when the inbound file was last ingested and when the outbound classified
            lines file was last written. Use the <strong>Run now</strong> button to trigger a
            manual sync outside the scheduled window. Each run is idempotent: if the inbound file
            hasn't changed since the last run, it is skipped safely.
          </p>

          <Subheading>Engagement</Subheading>
          <p className="text-sm text-gray-600">
            At <strong>Admin &gt; Engagement</strong> you can see whether the app is being used, when and by
            whom: logins, active users, estimated time in use and changes made, with a comparison against the
            previous period, an activity heatmap by day and hour (UK time) and a per-user table you can search,
            sort and export to CSV. It shows counts only, never cash flow figures. Test and QlickXL support
            accounts are left out unless you tick <strong>Include excluded accounts</strong>. Time in use is an
            estimate built from activity, and failed sign-in attempts are not recorded by the app.
          </p>
        </HelpSection>

        {/* 7. Roles */}
        <HelpSection title="Roles &amp; permissions" icon={<CheckCircle className="h-5 w-5" />}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-600 border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-semibold text-gray-900">Role</th>
                  <th className="text-left py-2 pr-4 font-semibold text-gray-900">Can do</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 pr-4 font-medium text-gray-800 whitespace-nowrap">User</td>
                  <td className="py-2">No access of their own. Access assignments (set by an Admin) grant Preparer (enter and submit) and/or Reviewer (agree and revert) rights per entity or per division; users see only the entities they are assigned to</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium text-gray-800 whitespace-nowrap">Group Finance</td>
                  <td className="py-2">Full access across all entities and periods (never restricted by assignments); can view audit log</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium text-gray-800 whitespace-nowrap">Admin</td>
                  <td className="py-2">Full access: CF codes, statement layout, Qlik exchange config, users</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Subheading>Per-entity access assignments</Subheading>
          <p className="text-sm text-gray-600 mb-2">
            On top of the base role, an Admin can assign each user to specific entities or whole
            divisions (a division assignment covers every entity in it, including entities added
            later). Each assignment carries its own role — <strong>Preparer</strong> or{' '}
            <strong>Reviewer</strong> — and a user can hold any mix: preparer for one entity,
            reviewer for another, or both roles on the same entity.
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>Assignments also control <strong>visibility</strong>: the entity list, validation board and system dashboard only show the entities a user's assignments cover.</li>
            <li>Entering figures and submitting requires a <strong>Preparer</strong> assignment on that entity.</li>
            <li>Agree, Revert and Reopen require a <strong>Reviewer</strong> assignment on that entity.</li>
            <li>A user with <strong>no assignments</strong> keeps full access (the original behaviour) until their first assignment is added.</li>
            <li><strong>Admin</strong> and <strong>Group Finance</strong> always have full access everywhere — assignments never restrict them.</li>
          </ul>
        </HelpSection>

        {/* 8. Glossary */}
        <HelpSection title="Glossary" icon={<BookA className="h-5 w-5" />}>
          <dl>
            <Term term="Period" definition="A fiscal month in YY-Pn format. The financial year starts in August (P1 = August). So 26-P1 = August 2025, 26-P12 = July 2026." />
            <Term term="Entity" definition="A legal reporting unit (company). Entity setup is managed in the month-end close app and synced into Cashflow automatically every hour — add an entity there and it appears here within the hour. (Its Qlik figures start flowing once an Admin maps its Qlik name.)" />
            <Term term="Division" definition="A grouping of entities. Division setup also lives in the month-end close app and syncs across on the same hourly schedule." />
            <Term term="CF code" definition="A classification code in the format CF1000–CF9999 that maps a cash flow movement to a specific type (capex, HP, loan, etc.)." />
            <Term term="Derived line" definition="A line whose value is populated automatically from Qlik and is read-only in Cashflow: balance-sheet movements (CF1000–CF4030), the P&L rows (CFP0500, CFP1000–CFP1030), and the bank movement (CF0100)." />
            <Term term="Assignment" definition="An Admin-granted link between a user and an entity or division, carrying a role (Preparer or Reviewer). Controls what a user can see, and who can enter, submit and agree each entity's cash flow." />
            <Term term="Session timeout" definition="You stay signed in while you're active. After 10 minutes of inactivity you're signed out automatically." />
            <Term term="Classified line" definition="A CF code (CF5000+) whose value is entered by the entity preparer." />
            <Term term="ECF (Entity Cash Flow)" definition="The record of all classified lines for a given entity and period, plus its status." />
            <Term term="Unreconciled" definition="Net cash change minus the balance-sheet bank movement (cash/bank accounts, from Qlik). The unreconciled cash balance — must be zero for the entity to be balanced." />
            <Term term="BS movement" definition="The cash movement shown on the balance sheet, sourced from Qlik." />
            <Term term="CF9999" definition="The catch-all code for unclassified historic balances. Must be zero before an entity can be agreed." />
            <Term term="Validation Board" definition="The colour-coded matrix showing the status of every entity across periods." />
            <Term term="Exchange Run" definition="A single Qlik CSV sync operation — either inbound (Qlik → Cashflow) or outbound (Cashflow → Qlik). Each run is audited and idempotent." />
            <Term term="Not Started" definition="No classified lines have been entered for this entity and period." />
            <Term term="In Progress" definition="At least one classified line has been entered but the preparer has not yet submitted." />
            <Term term="Submitted" definition="The preparer has submitted the cash flow for review. Entry fields are locked." />
            <Term term="Agreed" definition="A Reviewer for the entity (or a super user) has agreed the submission. The entity turns green on the Validation Board." />
          </dl>
        </HelpSection>

        {/* 9. Contact */}
        <HelpSection title="Contact support" icon={<Mail className="h-5 w-5" />}>
          <p className="text-sm text-gray-600 mb-4">
            If something isn't working as expected, or you have a question not covered here, email
            the finance team. We aim to reply within one business day.
          </p>
          <a href="mailto:support@ashcourt.co.uk">
            <Button>
              <Mail className="mr-1.5 h-4 w-4" />
              Email support@ashcourt.co.uk
            </Button>
          </a>
        </HelpSection>

      </div>
    </div>
  );
}
